/**
 * RecordingPlayer
 *
 * 상담/예약 상세 화면에서 공통으로 사용하는 녹음 재생/다운로드 UI.
 * 백엔드가 내려주는 recordingUrl이 null(서명 실패/처리 중/녹음 없음)인 경우
 * 재시도 버튼을 제공하며, /api/consults/{id}/recording-url로 재발급을 시도한다.
 */

import { useCallback, useState } from "react";
import { Download, Mic, RefreshCw, Loader2 } from "lucide-react";
import dayjs from "dayjs";
import { Button } from "@/components/ui/button";
import { consultApi } from "@/api/consult";
import { cn } from "@/lib/utils";

export interface RecordingPlayerProps {
  /** 서버가 내려준 signed URL. null 또는 undefined면 재시도 가능 상태로 표시 */
  recordingUrl: string | null | undefined;
  /** 재시도/다운로드 파일명에 사용 */
  consultId: number;
  /** 다운로드 파일명 timestamp. 없으면 현재 시각 사용 */
  occurredAt?: string | Date | null;
  /** 인라인(헤더 옆) 레이아웃으로 축소 표시 */
  compact?: boolean;
  /** 파일명 prefix (기본 "recording") */
  filenamePrefix?: string;
  /** 다운로드 버튼 숨김 */
  hideDownload?: boolean;
  /** 외곽 className */
  className?: string;
}

function buildDownloadFilename(
  consultId: number,
  occurredAt: string | Date | null | undefined,
  prefix = "recording",
): string {
  const ts = occurredAt
    ? dayjs(occurredAt).format("YYYYMMDD_HHmmss")
    : dayjs().format("YYYYMMDD_HHmmss");
  return `${prefix}_${consultId}_${ts}.wav`;
}

function triggerDownload(url: string, filename: string) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function RecordingPlayer({
  recordingUrl,
  consultId,
  occurredAt,
  compact = false,
  filenamePrefix,
  hideDownload = false,
  className,
}: RecordingPlayerProps) {
  const [retriedUrl, setRetriedUrl] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const effectiveUrl = retriedUrl ?? recordingUrl ?? null;

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const url = await consultApi.getRecordingUrl(consultId);
      setRetriedUrl(url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "녹음 URL을 불러올 수 없습니다";
      setRetryError(msg);
    } finally {
      setRetrying(false);
    }
  }, [consultId]);

  const handleDownload = useCallback(() => {
    if (!effectiveUrl) return;
    triggerDownload(
      effectiveUrl,
      buildDownloadFilename(consultId, occurredAt, filenamePrefix),
    );
  }, [effectiveUrl, consultId, occurredAt, filenamePrefix]);

  // 정상: URL 있음 → 재생 + 다운로드
  if (effectiveUrl) {
    if (compact) {
      return (
        <div className={cn("flex items-center gap-1", className)}>
          <audio
            controls
            src={effectiveUrl}
            className="h-8 min-w-[180px] max-w-[320px] flex-1"
          />
          {!hideDownload && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleDownload}
              aria-label="다운로드"
            >
              <Download className="h-3 w-3" />
            </Button>
          )}
        </div>
      );
    }
    return (
      <div className={cn("space-y-2", className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium flex items-center gap-1">
            <Mic className="h-4 w-4" />
            녹음 파일
          </span>
          {!hideDownload && (
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={handleDownload}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              다운로드
            </Button>
          )}
        </div>
        <audio controls src={effectiveUrl} className="w-full">
          브라우저가 오디오 재생을 지원하지 않습니다.
        </audio>
      </div>
    );
  }

  // URL 없음 → 재시도 가능 상태
  return (
    <div
      className={cn(
        "flex items-center gap-2 text-xs text-muted-foreground",
        compact ? "px-2" : "px-3 py-2 rounded border bg-muted/30",
        className,
      )}
    >
      <Mic className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 truncate">
        {retryError
          ? `녹음 불러오기 실패: ${retryError}`
          : "녹음 파일이 아직 준비되지 않았거나 불러올 수 없습니다."}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 text-xs"
        onClick={handleRetry}
        disabled={retrying}
      >
        {retrying ? (
          <>
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            재시도 중
          </>
        ) : (
          <>
            <RefreshCw className="h-3 w-3 mr-1" />
            재시도
          </>
        )}
      </Button>
    </div>
  );
}

export default RecordingPlayer;
